import Vue from 'vue';
import BootstrapVue from 'bootstrap-vue';
import VueMoment from 'vue-moment';
import moment from 'moment-timezone';
import vSelect from 'vue-select';
import Multiselect from 'vue-multiselect';
import VueJsonPretty from 'vue-json-pretty';
import VueLogger from 'vuejs-logger';

import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';

// eslint-disable-next-line import/no-unresolved
import Keycloak from 'keycloak-js';

import App from './App';
import router from './router';
import store from './store';
import http from './api';

import 'vue-select/dist/vue-select.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';

import config from './utils/config';
import vuetify from './plugins/vuetify';
import i18n from './plugins/i18n';

/* eslint-disable no-unused-expressions */
if (config.styleName === 'aurus') {
  import('@/assets/styles/aurus.scss');
} else if (config.styleName === 'sollers') {
  import('@/assets/styles/sollers.scss');
} else if (config.styleName === 'argo') {
  import('@/assets/styles/argo.scss');
}

/* eslint-enable no-unused-expressions */

Vue.use(BootstrapVue);
Vue.use(VueMoment, {
  moment,
});
Vue.moment.locale(localStorage.getItem('locale') || 'en');

const isProduction = process.env.NODE_ENV === 'production';

const loggerOptions = {
  isEnabled: !isProduction || !!config.logLevel,
  logLevel: config.logLevel || (isProduction ? 'error' : 'debug'),
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: false,
  separator: '|',
  showConsoleColors: true,
};

const numberFormatter = (value) => {
  if (typeof value !== 'number') {
    return 0;
  }
  return new Intl.NumberFormat('ru-RU').format(value);
}

Vue.use(VueLogger, loggerOptions);

Vue.component('vue-select', vSelect);
Vue.component('multiselect', Multiselect);
Vue.component('vue-json-pretty', VueJsonPretty);

Vue.filter('numberFormatter', numberFormatter);

Vue.prototype.$numberFormatter = numberFormatter;
Vue.prototype.$config = config;
Vue.prototype.$http = http;
Vue.config.productionTip = false;

if (config.SENTRY_DSN) {
  Sentry.init({
    Vue,
    dsn: config.SENTRY_DSN,
    ...(config.SENTRY_TUNNEL ? { tunnel: config.SENTRY_TUNNEL } : {}),
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    logErrors: true,
  });

  Vue.prototype.$sentry = Sentry;
}

const $keycloak = {
  config: config.SSO_FRONT_CONFIG ? JSON.parse(config.SSO_FRONT_CONFIG) : null,
  inited: false,
  realms: [],
  adapters: {},
  async initRealm(realm) {
    const adapter = this.adapters[realm];

    if (!adapter) { return null; }

    try {
      await adapter.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        checkLoginIframeInterval: 1,
        enableLogging: true,
        useNonce: false,
      });
      return adapter.authenticated;
    } catch (e) {
      // realms.splice(realms.findIndex((elm) => (elm.realm === realm)), 1);
      return null;
    }
  },
};

if ($keycloak.config) {
  $keycloak.config.forEach((realmConfig) => {
    $keycloak.adapters[realmConfig.realm] = new Keycloak(realmConfig);
  });
}

Vue.prototype.$keycloak = $keycloak;

const app = (async () => {
  Vue.$log.debug('start');
  let ssoAuthenticated = false;

  const realm = localStorage.getItem('realm');
  if (realm) {
    Vue.$log.debug('realm: ', realm);
    const adapter = $keycloak.adapters[realm];
    if (adapter) {
      Vue.$log.debug('match adapter');
      try {
        ssoAuthenticated = await $keycloak.initRealm(realm);

        if (ssoAuthenticated) {
          Vue.$log.debug('Authenticated');
          localStorage.setItem('jwtacc', adapter.token);
        } else {
          Vue.$log.debug('Not authenticated');
          localStorage.removeItem('jwtacc');
        }
      } catch (err) {
        Vue.$log.debug('Error init realm', err);
        localStorage.removeItem('realm');
        localStorage.removeItem('jwtacc');
      }
    } else {
      Vue.$log.debug('Unknwon realm', realm);
      localStorage.removeItem('realm');
    }
  }

  const jwtacc = localStorage.getItem('jwtacc');
  if (jwtacc) {
    try {
      await store.dispatch('performAuth', { jwtacc, app: Vue.prototype });
    } catch (err) {
      if (ssoAuthenticated) {
        const { data = {} } = err.response || {};

        const errorUrl = data.name && data.type ? `${data.name}.${data.type}` : (data.message || err.message);
        Vue.$log.debug(err.message, errorUrl);
        window.history.replaceState(null, '', `/sign-in/not-registred?error=${errorUrl}`);
      } else {
        localStorage.removeItem('jwtacc');
      }
    }
  }

  Vue.prototype.$toast = function $toast(message, options = {}) {
    this.$bvToast.toast(unescape(message), {
      title: this.$t('toast.success'),
      variant: 'success',
      solid: true,
      autoHideDelay: 15000,
      ...options,
    });
  };

  Vue.$log.debug('Vue init ---');

  const ref = new Vue({
    el: '#app',
    store,
    router,
    i18n,
    vuetify,
    render: (h) => h(App),
  });

  http.$app = ref;

  return ref;
})();

export default app;
